import { useEffect, useState } from 'react';
import GeoCombobox from '../../../components/common/GeoCombobox';
import bdUpazilas from '../../../data/bd-upazilas.json';

/**
 * Post office picker for one address block. Choosing an office from the list fills its English name, its Bangla
 * name and its post code at once (government forms ask for both scripts), so the operator never types the Bangla
 * post office. The list is the address's district; the offices of the chosen upazila come first. A name that is
 * not in the list can still be typed (the Bangla name then stays empty).
 *
 * Catalogue: src/data/bd-post-offices.json (scripts/build-post-offices.mjs), loaded only when a picker mounts.
 */

let catalogue = null;
const loadCatalogue = () => (catalogue ||= import('../../../data/bd-post-offices.json').then((m) => m.default.districts));

// Thana names in the postal list and upazila names in ours are spelled independently ("বকশীগঞ্জ" / "বকশিগঞ্জ").
const looseBn = (s) => String(s || '')
    .normalize('NFC')
    .replace(/[\s‌‍]/g, '')
    .replace(/ী/g, 'ি')
    .replace(/ূ/g, 'ু');
const looseEn = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '').replace(/bogra/g, 'bogura');

function useCatalogue() {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        loadCatalogue().then((d) => { if (alive) setData(d); }).catch(() => {});
        return () => { alive = false; };
    }, []);
    return data;
}

/** The district's offices as combobox options, the upazila's own first. */
export function officeOptions(data, district, upazila, lang) {
    const rows = data?.[district] || [];
    const upazilaEn = bdUpazilas.find((u) => u.bn_name === upazila)?.name;
    const inUpazila = (r) => Boolean(upazila) && (looseBn(r[3]) === looseBn(upazila) || (upazilaEn && looseEn(r[4]) === looseEn(upazilaEn)));
    return [...rows]
        .sort((a, b) => Number(inUpazila(b)) - Number(inUpazila(a)))
        .map(([code, en, bn]) => ({
            value: `${code}-${en}`,
            label: lang === 'bn' ? `${bn} (${en})` : `${en} (${bn})`,
            hint: code,
            code, en, bn,
        }));
}

export default function PostOfficePicker({ value, bangla, district, upazila, lang, placeholder, banglaNote, onType, onPick }) {
    const data = useCatalogue();
    return (
        <div>
            <GeoCombobox
                value={value}
                onChange={onType}
                onSelectOption={onPick}
                options={officeOptions(data, district, upazila, lang)}
                placeholder={placeholder}
            />
            {bangla && <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{banglaNote(bangla)}</p>}
        </div>
    );
}
